import { Button, Drawer, Group, Modal, ScrollArea, Stack, Text, Title } from '@mantine/core'
import { forwardRef, PropsWithChildren, ReactNode } from 'react'

import { useMediaQuery } from '@mantine/hooks'

import styles from './styles.module.scss';

type UnifiedDialogProps = {
  title: ReactNode
  body?: ReactNode
  footer?: ReactNode
  description?: ReactNode
  onClose: () => void
  opened: boolean
  onOk?: () => void
  loading?: boolean
  okText?: string
  okRightSection?: ReactNode
  onCancel?: () => void
  cancelText?: string
  closable?: boolean
  danger?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

// Return either BottomSheet or Modal depending on client platform
export const UnifiedDialog = forwardRef<HTMLDivElement, PropsWithChildren<UnifiedDialogProps>>(({ closable = true, ...props }, ref) => {
  const isMobile = useMediaQuery("(max-width: 768px)")

  const buttonsView = (
    <>
      {props.onOk && (
        <Button
          size="md"
          onClick={props.onOk}
          fullWidth
          variant={props.danger ? 'outline' : undefined}
          color={props.danger ? '#B51D1D' : undefined}
          loading={props.loading}
          rightSection={props.okRightSection}
        >
          {props.okText}
        </Button>
      )}
      {props.onCancel && (
        <Button fullWidth variant='default' onClick={props.onCancel}>
          {props.cancelText}
        </Button>
      )}
    </>
  )

  if (isMobile) {
    return (
      <Drawer.Root
        closeOnClickOutside={closable}
        closeOnEscape={closable}
        opened={props.opened}
        onClose={props.onClose}
        position="bottom"
        w="100%"
      >
        <Drawer.Overlay />
        <Drawer.Content
          w="100%"
          className={styles.drawerContent}
        >
          <Drawer.Header>
            <Drawer.Title>
              <Title order={3} fw={500}>
                {props.title}
              </Title>
            </Drawer.Title>
            {closable && <Drawer.CloseButton />}
          </Drawer.Header>
          <Drawer.Body flex={1} className={styles.drawerBody}>
            <Stack flex={1} h="100%" gap="lg">
              <ScrollArea>
                {props.body}

              </ScrollArea>
              <Group flex={1} align='flex-end' wrap='nowrap' >
                {buttonsView}
              </Group>
            </Stack>

          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>

    )
  }

  return (
    <Modal
      onClose={props.onClose}
      opened={props.opened}
      closeOnClickOutside={closable}
      closeOnEscape={closable}
      withCloseButton={closable}
      ref={ref}
      centered
      radius='md'
      size={props.size ?? undefined}
      pos='relative'
      title={
        <Text size='lg' fw={500}>
          {props.title}
        </Text>
      }
    >
      <Stack flex={1} h="100%" gap="lg">
        {props.body}

        <Group wrap='nowrap'>
          {buttonsView}
        </Group>
      </Stack>
    </Modal>
  )
})
